import {
  NJButton,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AdminAgent } from "@/types";
import { useAdminAgents, useDeleteAdminAgent } from "./useAdminAgents";

const PAGE_SIZE = 20;

function StatusCell({ data }: ICellRendererParams<AdminAgent>) {
  if (!data) return null;
  return <StatusBadge status={data.status} />;
}

function ActionsCell({ data }: ICellRendererParams<AdminAgent>) {
  const navigate = useNavigate();
  const deleteAgent = useDeleteAdminAgent();
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!data) return null;

  return (
    <div className="flex items-center gap-1">
      <NJIconButton
        icon="edit"
        variant="tertiary"
        label={`Edit ${data.name}`}
        onClick={() => navigate(`/admin/agents/${data.slug}/edit`)}
      />
      <NJIconButton
        icon="delete"
        variant="destructive"
        label={`Delete ${data.name}`}
        isDisabled={deleteAgent.isPending}
        onClick={() => setConfirmOpen(true)}
      />
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete agent"
        isPending={deleteAgent.isPending}
        onConfirm={() =>
          deleteAgent.mutate(data.slug, {
            onSuccess: () => setConfirmOpen(false),
          })
        }
      >
        Delete agent <strong>{data.name}</strong>? This also removes its access
        requests, sessions and groups.
      </ConfirmModal>
    </div>
  );
}

/** Admin agents table — badge status, icon actions, server-side search + pagination. */
export function AdminAgentsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query]);

  const { data, isLoading, isError } = useAdminAgents({
    q: query,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  const columnDefs = useMemo<ColDef<AdminAgent>[]>(
    () => [
      {
        headerName: "Slug",
        field: "slug",
        flex: 1,
        cellRenderer: (params: { data?: AdminAgent }) =>
          params.data ? (
            <Link
              to={`/admin/agents/${encodeURIComponent(params.data.slug)}`}
              className="text-nj-brand-solid-default no-underline hover:underline"
            >
              {params.data.slug}
            </Link>
          ) : null,
      },
      { headerName: "Name", field: "name", flex: 1 },
      { headerName: "Maintainer", field: "maintainer", flex: 1 },
      {
        headerName: "Status",
        cellRenderer: StatusCell,
        flex: 1,
        sortable: false,
      },
      { headerName: "Users", field: "usageMembersCount", flex: 1 },
      { headerName: "Validators", field: "validatorMembersCount", flex: 1 },
      {
        headerName: "Actions",
        cellRenderer: ActionsCell,
        flex: 1,
        sortable: false,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <NJHeading as="h1" scale="lg">
          Administration — agents
        </NJHeading>
        <Link to="/admin/agents/new">
          <NJButton label="Create agent" icon="add" />
        </Link>
      </div>

      <NJInputSearch
        label="Search agents"
        placeholder="slug, name, maintainer"
        value={searchInput}
        onChange={(event) => setSearchInput(event.currentTarget.value)}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <NJSpinner aria-label="Loading agents" />
        </div>
      ) : isError ? (
        <NJInlineMessage variant="error">
          Could not load the agents.
        </NJInlineMessage>
      ) : (
        <>
          <DataTable<AdminAgent>
            rowData={data?.items ?? []}
            columnDefs={columnDefs}
          />
          {data && data.items.length === 0 && (
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              No agent matches this search.
            </NJText>
          )}
          {totalPages > 1 && (
            <div className="flex justify-end">
              <NJPagination
                count={totalPages}
                currentItem={page}
                ariaLabelPrevious="Previous agents page"
                ariaLabelNext="Next agents page"
                onChange={(item: number) => setPage(item)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
